import { FormInitializerReturnTypeZod, ValidationMessageKeys } from 'src/Configuration/validation'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useMemo } from 'react'
import { z } from 'zod'
import { Installation, LocationConfigurationFormModel } from '../../models/installation.model'

const MIN_STREET_LENGTH = 3
const MIN_CITY_LENGTH = 3
const MAX_ADDRESS_LENGTH = 50
const MAX_ZIP_LENGTH = 10

export const useLocationConfigurationFormInitializer = (
  installation?: Installation
): FormInitializerReturnTypeZod<LocationConfigurationFormModel, LocationConfigurationFormModel> => {
  const { t } = useCustomTranslation()

  const initialValues = useMemo<LocationConfigurationFormModel>(
    () => ({
      streetName: installation?.streetName ?? '',
      streetNumber: installation?.streetNumber ?? '',
      zip: installation?.zip ?? '',
      city: installation?.city ?? '',
      countryCode: installation?.countryCode ?? '',
    }),
    [installation?.city, installation?.countryCode, installation?.streetName, installation?.streetNumber, installation?.zip]
  )

  const requiredString = (): z.ZodString =>
    z
      .string({ message: t(ValidationMessageKeys.required) })
      .trim()
      .min(1, t(ValidationMessageKeys.required))

  const validationSchema = z.object({
    streetName: requiredString()
      .min(MIN_STREET_LENGTH, t(ValidationMessageKeys.lengthMin, { value: MIN_STREET_LENGTH }))
      .max(MAX_ADDRESS_LENGTH, t(ValidationMessageKeys.lengthMax, { value: MAX_ADDRESS_LENGTH })),
    streetNumber: requiredString().max(MAX_ADDRESS_LENGTH, t(ValidationMessageKeys.lengthMax, { value: MAX_ADDRESS_LENGTH })),
    zip: requiredString().max(MAX_ZIP_LENGTH, t(ValidationMessageKeys.lengthMax, { value: MAX_ZIP_LENGTH })),
    city: requiredString()
      .min(MIN_CITY_LENGTH, t(ValidationMessageKeys.lengthMin, { value: MIN_CITY_LENGTH }))
      .max(MAX_ADDRESS_LENGTH, t(ValidationMessageKeys.lengthMax, { value: MAX_ADDRESS_LENGTH })),
    countryCode: requiredString(),
  })

  return { initialValues, validationSchema }
}
